<template>
  <div class="scroller">
    <div class="calendarmask" ref="calendarmask">
      <div class="navigation">
        <BackNavigation
          style="z-index: 9999;"
          @click="goToYearView"
          :text="currentYear"
          v-if="currentView == 1" />
      </div>
      <MonthDragger
        ref="monthDragger"
        @visible-months="checkCurrentYearForVisibleMonths"
        @set-data="emitSetData"
        v-if="currentView == 1"
        :selectedWeek="selectedWeek"
        :limitYears="true"
        :yearsInRange="years"
        :miny="miny"
        :maxy="maxy"
        :months="months" />
      <YearDragger 
        v-if="currentView == 2" 
        @set-month="changeToMonthView"
        :currentView="currentView"
        :currentYear="currentYear"
        :limitYears="true"
        :yearsInRange="years"/>
    </div>
  </div>
</template>

<script>
import BackNavigation from "@/components/calendarScroller/backnavigation.vue";
import MonthItem from "@/components/calendarScroller/monthItem.vue";
import YearItem from "@/components/calendarScroller/yearItem.vue";
import MonthDragger from "@/components/calendarScroller/MonthDragger.vue";
import YearDragger from "@/components/calendarScroller/yearDragger.vue";
import useCalendar from "@/components/calendarScroller/calendar.js";
import useUiScroller from "@/components/calendarScroller/uiscroller.js";


export default {
  emits:["setData"],
  props:["cdate","startyear","endyear","startview","monthmode"],
  components: {
    BackNavigation,
    MonthItem,
    YearItem,
    MonthDragger,
    YearDragger
  },
  setup(props){
    const form  = useCalendar();
    const uiscroller = useUiScroller();
    return {
      ...form,
      ...uiscroller
    }
  },
  data() {
    const months = [];
    const years = [];
    const daysOfWeek = ["S", "T", "Q", "Q", "S", "S", "D"];
    const selectedWeek = null;
    return {
      months,
      daysOfWeek,
      observer: null,
      currentYear: null,
      currentView: 1,
      years: years,
      selectedWeek,
      initialYear: null,
      yearsLimitedRange: true,
      miny: 0,
      maxy: 0
    };
  },
  computed:{
    afterMinYearCalendarIndex(){
      const sortedYears = [...this.years].sort((a,b) => {
        return a.year - b.year;
      });
      return sortedYears[1].year;
    },
    beforeMaxYearCalendarIndex(){
      const sortedYears = [...this.years].sort((a,b) => {
        return a.year - b.year;
      });
      return sortedYears[sortedYears.length-2].year;
    },
    afterMinCalendarIndex(){
      const sortedMonths = [...this.months];
      sortedMonths.sort((a, b) => {
        return a.index - b.index;
      });
      return sortedMonths[1].index;
    },
    beforeMaxCalendarIndex(){
      const sortedMonths = [...this.months];
      sortedMonths.sort((a, b) => {
        return a.index - b.index;
      });
      return sortedMonths[sortedMonths.length-2].index;
    },
    sortedMonths(){
      const sortedMonths = [...this.months];
      sortedMonths.sort((a, b) => {
        return a.index - b.index;
      });
      return sortedMonths;
    }
  },
  created(){
    if(this.monthmode){
      this.currentView = 2;
    }
  },
  mounted() {
    const date = this.cdate.start ?? new Date();
    this.currentYear = date.getFullYear();
    this.setMonthsInRange(date);
    this.$nextTick(() => this.observeMonths());
    this.initialYear = new Date().getFullYear();
    this.setYearsInRange(new Date());
  },
  methods: {
    checkCurrentYearForVisibleMonths(monthElements) {
      const maskRect = this.$refs.calendarmask.getBoundingClientRect();
      let mostVisibleMonth = null;
      let maxVisibleArea = 0;
      for (let i = 0; i < monthElements.length; i++) {
        if(monthElements[i] != null){
          const month = monthElements[i].month;
          const monthRef = monthElements[i].element;
          const monthRect = monthRef.getBoundingClientRect();
          const overlap = {
            top: Math.max(maskRect.top, monthRect.top),
            bottom: Math.min(maskRect.bottom, monthRect.bottom),
          };
          const visibleHeight = overlap.bottom - overlap.top;
          if (visibleHeight > maxVisibleArea) {
            maxVisibleArea = visibleHeight;
            mostVisibleMonth = month;
          }
        } 
      }
      if (mostVisibleMonth) {
        this.currentYear = mostVisibleMonth.year;
      }
    },
    emitSetData(data) {
      this.selectedWeek = data;
      this.$emit("setData", data);
    },
    changeToMonthView(data) {
      const { year, month } = data;
      const updatedDate = new Date(year,month,1);
      if(this.monthmode){
        const date = {
          start: updatedDate,
          end: new Date(year, month+1, 0)
        }
        this.selectedWeek = date;
        this.$emit("setData", date);
      }else{
        this.currentYear = updatedDate.getFullYear();
        this.initialYear = this.currentYear;
        this.setMonthsInRange(updatedDate);
        this.currentView = 1;
        this.$nextTick(() => this.observeMonths());
      }
    },
    goToYearView() {
      this.currentView = 2;
      this.$nextTick(() => {
        this.observeYears();
      });
    },
    observeYears() {
      if(!this.yearsLimitedRange){
        const headers = document.querySelectorAll(".year");
        const options = this.getObserverOptions();
        const observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            let year = entry.target.getAttribute("year");
            if (entry.isIntersecting) {
              if(year == this.afterMinYearCalendarIndex){
                this.addPrevious3Years();
              }
              if(year == this.beforeMaxYearCalendarIndex){
                this.addNext3Years();
              }
              this.$nextTick(() => {
                const newHeaders = document.querySelectorAll(".year");
                newHeaders.forEach((header) => observer.observe(header));
              });
            }
          });
        }, options);
        headers.forEach(header => observer.observe(header));
      }
    },
    observeMonths() {
      const headers = document.querySelectorAll(".month");
      const options = this.getObserverOptions();
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          let arrindex = entry.target.getAttribute("arrindex");
          if (entry.isIntersecting) {
            if(arrindex == this.afterMinCalendarIndex){
              this.addPrevious3Months();
            }
            if(arrindex == this.beforeMaxCalendarIndex){
              this.addNext3Months();
            }
            this.$nextTick(() => {
              const newHeaders = document.querySelectorAll(".month");
              newHeaders.forEach((header) => this.observer.observe(header));
            });
          }
        });
      }, options);
      headers.forEach((header) => this.observer.observe(header));
    },
    getObserverOptions() {
      const percentageOfElementVisibleOnScreen = 0.2;
      return {
        root: this.$refs.calendar,
        rootMargin: "0px",
        threshold: percentageOfElementVisibleOnScreen,
      };
    },
    setMonthsInRange(currentDate) {
      let months = this.calculateMonthsInRange(currentDate)
      this.calculatePositionsForCurrentMonths(months)
      this.months = months;
      if(this.yearsLimitedRange){
        this.miny = this.calculatePositionForFirstMonthIfLimit(this.startyear,currentDate);
        this.maxy = this.calculatePositionForLastMonthIfLimit(this.endyear,currentDate);
      }
    },
    addPrevious3Months() {
      const {year, month} = this.sortedMonths[0];
      const currentDate = new Date(year,month-1,1);
      const yl = this.yearsLimitedRange ? this.years[0].year : -1;
      const months = this.calculatePrevious3Months(this.afterMinCalendarIndex,currentDate,yl);
      this.months = [...months,...this.months];
      this.calculatePositionsForCurrentMonths(this.months);
    },
    addNext3Months() {
      const {year, month} = this.sortedMonths[this.months.length - 1];
      const currentDate = new Date(year,month-1,1);
      const yl = this.yearsLimitedRange ? this.years[this.years.length-1].year : -1;
      const months = this.calculateNext3Months(this.beforeMaxCalendarIndex,currentDate,yl);
      this.months = [...this.months,...months];
      this.calculatePositionsForCurrentMonths(this.months);
    },
    setYearsInRange(currentDate) {
      this.years = this.calculateYearsInRange(currentDate);
      this.calculateYearsPositions(this.years,this.initialYear);
    },
    addPrevious3Years() {
      var years = this.calculatePrevious3Years(this.years)
      this.years = [...years, ...this.years];
      this.calculateYearsPositions(this.years,this.initialYear);
    },
    addNext3Years() {
      var years = this.calculateNext3Years(this.years)
      this.years = [...this.years, ...years];
      this.calculateYearsPositions(this.years,this.initialYear);
    }
  },
};
</script>

<style scoped>
.scroller{
  z-index: 9999;
  padding-left: 30px;
  padding-right: 30px;
}

.navigation {
  display: flex;
  flex: 1;
  padding-bottom: 10px;
  padding-top: 10px;
  background: white;
  position: relative;
  z-index: 9999999;
}

.calendarmask{
  overflow: hidden;
}

.calendar {
  overflow-y: visible;
  height: 100vh;
  padding: 16px;
  padding-left: 0;
  padding-right: 0;
  -ms-overflow-style: none;
  scrollbar-width: none; 
  position: relative;
}

.calendar::-webkit-scrollbar {
  display: none;
}
</style>
